import React, { useState, useEffect, useMemo, useRef } from "react";
import { Flex, Text, Select, IconButton, Button, Dialog, Switch } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { ActiveIntegrateCard } from "components";
import {
  useGetLeatMappings,
  useGetLeatShops,
  useSaveLeatMappings,
  useUpdateLeat,
  useDeactivateLeat,
  useUpdateLeatVisitsSettings,
  useGetLeatContactAttributes,
  useUpdateLeatFieldMappings,
  useGetAllBrands,
} from "hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useProfileContext, useLocationContext } from "context";
import { useToast } from "../../../context/ToastContext";
import { PermissionAlertDialog } from "components";
import { LeatDataFieldsSection } from "./LeatDataFieldsSection";
import { normalizeLeatFieldMappings } from "./leatFieldMappings";

export function ActiveLeatIntegration() {
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { initialItems, locationsData } = useLocationContext();
  const { setToastMessage } = useToast();
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Check if user is owner
  const isOwner = userProfile.user_role === "Owner";

  const [apiKey, setApiToken] = useState("");
  const [originalApiToken, setOriginalApiToken] = useState(""); // track the stored (masked) API token
  const [isApiSaving, setIsApiSaving] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [hasStoredApiToken, setHasStoredApiToken] = useState(false); // whether there's a saved (but masked) key

  // Inline validation state (for field-level UX)
  const [isApiTokenValid, setIsApiTokenValid] = useState(true);
  const [apiValidationMessage, setApiValidationMessage] = useState(null);
  const [apiSaveSuccess, setApiSaveSuccess] = useState(false);

  const [visitsEnabled, setVisitsEnabled] = useState(false);
  const [isVisitsSaving, setIsVisitsSaving] = useState(false);
  const [fieldMappings, setFieldMappings] = useState(() => normalizeLeatFieldMappings(null));
  const [originalFieldMappings, setOriginalFieldMappings] = useState(() =>
    normalizeLeatFieldMappings(null)
  );
  const [isFieldsSaving, setIsFieldsSaving] = useState(false);

  // separate loading flags for each save
  const [isBrandsSaving, setIsBrandsSaving] = useState(false);
  const [isIndependentSaving, setIsIndependentSaving] = useState(false);

  const { data: brandsData, isLoading: isBrandsLoading } = useGetAllBrands(userProfile?.organisation_id);

  const { data: leatMappingsData, isLoading: isMappingsLoading } = useGetLeatMappings(userProfile?.organisation_id);
  const { data: leatShopsData, isLoading: isShopsLoading } = useGetLeatShops(userProfile?.organisation_id);
  const { data: leatAttributesData } = useGetLeatContactAttributes(userProfile?.organisation_id);

  const saveLeatMappings = useSaveLeatMappings();
  const deactivateLeat = useDeactivateLeat();
  const updateLeat = useUpdateLeat();
  const updateLeatVisitsSettings = useUpdateLeatVisitsSettings();
  const updateLeatFieldMappings = useUpdateLeatFieldMappings();

  // Local lists / state
  const [leatShops, setLeatShops] = useState([]); // normalized shops: { id, name }
  const [bconnectBrands, setBconnectBrands] = useState([]); // non-default brands
  const [independentLocations, setIndependentLocations] = useState([]);

  // single mapping state keyed by bconnect_location_id -> { leat_shop_uuid, isMapped }
  const [locationMappings, setLocationMappings] = useState({});
  const [originalLocationMappings, setOriginalLocationMappings] = useState({});

  // Per-brand temporary selection for "apply to brand" (optional usage in this screen)
  const [brandListSelections, setBrandListSelections] = useState({});

  const originalLocationMappingsRef = useRef(originalLocationMappings);
  useEffect(() => {
    originalLocationMappingsRef.current = originalLocationMappings;
  }, [originalLocationMappings]);

  // handle API token change
  const handleApiTokenChange = (e) => {
    const value = e.target.value ?? "";
    setApiToken(value);
    setIsApiTokenValid(true);
    setApiValidationMessage(null);
    setApiSaveSuccess(false);
  };

  const handleSaveApiToken = async () => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      setApiSaveSuccess(false);
      return;
    }

    // If the input currently contains masking chars, treat as "no change"
    if (apiKey && apiKey.includes("*")) {
      setIsApiTokenValid(false);
      setApiValidationMessage("Please enter a new API token.");
      setApiSaveSuccess(false);
      setToastMessage("No changes to API token");
      return;
    }

    if (!apiKey || apiKey.trim() === "") {
      setIsApiTokenValid(false);
      setApiValidationMessage("Please enter a valid API token.");
      setApiSaveSuccess(false);
      setToastMessage("API token cannot be empty");
      return;
    }

    setIsApiTokenValid(true);
    setApiValidationMessage(null);
    setIsApiSaving(true);
    try {
      const res = await updateLeat.mutateAsync({
        organisation_id: userProfile.organisation_id,
        api_token: apiKey.trim(),
      });

      if (res?.data?.status === "success") {
        // queryClient.invalidateQueries(["leat-mappings", userProfile.organisation_id]);
        // queryClient.invalidateQueries(["leat-status", userProfile.organisation_id]);
        queryClient.invalidateQueries(["leat-shops", userProfile.organisation_id]);

        // reset to masked placeholder post-save
        const masked = "********";
        setApiToken(masked);
        setOriginalApiToken(masked);
        setHasStoredApiToken(true);
        setIsApiTokenValid(true);
        setApiValidationMessage(null);
        setApiSaveSuccess(true);
        setToastMessage("API token validated and saved");
      } else {
        const msg = "Failed to update API token. Please try again shortly or contact support.";
        setIsApiTokenValid(false);
        setApiValidationMessage(msg);
        setToastMessage(msg);
        setApiSaveSuccess(false);
      }
    } catch (err) {
      const msg = "Failed to update API token. Please try again shortly or contact support.";
      setIsApiTokenValid(false);
      setApiValidationMessage(msg);
      setToastMessage(msg);
      setApiSaveSuccess(false);
    } finally {
      setIsApiSaving(false);
    }
  };

  // handle save location mappings
  const handleSaveLocationMappings = async (section) => {
    const savingSetters =
      section === "brands"
        ? [setIsBrandsSaving]
        : section === "independent"
        ? [setIsIndependentSaving]
        : [setIsBrandsSaving, setIsIndependentSaving];

    savingSetters.forEach((s) => s(true));

    try {
      const allLocations = [
        ...(independentLocations || []),
        ...(Object.values(brandLocationsMap || {}).flat() || []),
      ];

      const locationPayload = (allLocations || [])
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currList = curr.leat_shop_uuid || null;

          if (!currList) return null;

          const leatListObj = leatShops.find((bl) => String(bl.id) === String(currList));
          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            leat_shop_uuid: currList,
            leat_shop_name: leatListObj?.name || "",
          };
        })
        .filter(Boolean);

      const payload = {
        organisation_id: userProfile.organisation_id,
        brand_mappings: [],
        location_mappings: locationPayload,
      };

      const result = await saveLeatMappings.mutateAsync(payload);

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      setToastMessage("Location mappings saved successfully");
      queryClient.invalidateQueries(["leat-mappings", userProfile.organisation_id]);
    } catch (err) {
      console.error("Error saving Leat location mappings:", err);
      setToastMessage(err?.message || "Failed to save location mappings");
    } finally {
      savingSetters.forEach((s) => s(false));
    }
  };

  // handle disconnect
  const handleDeactivate = async () => {
    setIsDeactivating(true);
    try {
      const res = await deactivateLeat.mutateAsync({ organisation_id: userProfile.organisation_id });
      if (res?.data?.status !== "success") {
        throw new Error(res?.data?.message || "Failed to deactivate integration");
      }

      // clear local state
      setIsDeactivating(false);
      setShowConfirmDialog(false);
      setToastMessage("Leat integration disconnected");
      setHasStoredApiToken(false);
      setLocationMappings({});
      setLeatShops([]);

    //   queryClient.invalidateQueries(["leat-mappings", userProfile.organisation_id]);
      queryClient.invalidateQueries(["leat-status", userProfile.organisation_id]);
    //   queryClient.invalidateQueries(["leat-shops", userProfile.organisation_id]);
    } catch (err) {
      console.error("Error deactivating Leat:", err);
      setToastMessage(err?.message || "Failed to deactivate Leat integration");
      setIsDeactivating(false);
    }
  };

  // Populate lists and mappings from backend when available
  useEffect(() => {
    // if there's no server payload, clear both local and original states
    if (!leatMappingsData) {
      setLocationMappings({});
      setOriginalLocationMappings({});
      originalLocationMappingsRef.current = {};
      return;
    }

    const mappings = leatMappingsData.data.mappings?.location_mappings || [];

    // build a serverMap: { "<locId>": { leat_shop_uuid: "..." } }
    const serverMap = {};
    mappings.forEach((m) => {
      const key = String(m.bconnect_location_id);
      serverMap[key] = { leat_shop_uuid: m.leat_shop_uuid || "" };
    });

    // Build the authoritative state from server data
    const newLocationMappings = {};
    Object.keys(serverMap).forEach((key) => {
      newLocationMappings[key] = {
        leat_shop_uuid: serverMap[key].leat_shop_uuid,
        isMapped: Boolean(serverMap[key].leat_shop_uuid),
      };
    });

    // Always replace local state with server state (source of truth)
    setLocationMappings(newLocationMappings);

    // Update originals to match server state
    const newOriginals = {};
    Object.keys(serverMap).forEach((k) => {
      newOriginals[k] = { leat_shop_uuid: serverMap[k].leat_shop_uuid };
    });
    setOriginalLocationMappings(newOriginals);
    originalLocationMappingsRef.current = newOriginals;

    if (leatMappingsData.data.api_token) {
      setApiToken(leatMappingsData.data.api_token);
      setOriginalApiToken(leatMappingsData.data.api_token);
      setHasStoredApiToken(true);
    } else {
      setHasStoredApiToken(false);
      setOriginalApiToken("");
      setApiToken("");
    }

    setVisitsEnabled(Boolean(leatMappingsData.data.visits_enabled));

    const nextFieldMappings = normalizeLeatFieldMappings(leatMappingsData.data.field_mappings);
    setFieldMappings(nextFieldMappings);
    setOriginalFieldMappings(nextFieldMappings);
  }, [leatMappingsData]);

  const handleVisitsToggle = async (checked) => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      return;
    }
    setIsVisitsSaving(true);
    try {
      const res = await updateLeatVisitsSettings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        visits_enabled: checked,
      });
      if (res?.data?.status === "success") {
        setVisitsEnabled(Boolean(res.data.visits_enabled));
        queryClient.invalidateQueries(["leat-mappings", userProfile.organisation_id]);
        queryClient.invalidateQueries(["leat-status", userProfile.organisation_id]);
        setToastMessage(checked ? "Visit sync enabled" : "Visit sync disabled");
      } else {
        setToastMessage("Failed to update visit sync settings");
      }
    } catch (err) {
      setToastMessage("Failed to update visit sync settings");
    } finally {
      setIsVisitsSaving(false);
    }
  };

  const handleFieldMappingChange = (source, value) => {
    setFieldMappings((prev) => ({ ...prev, [source]: value }));
  };

  const handleSaveFieldMappings = async () => {
    if (!userProfile?.organisation_id) {
      setToastMessage("Organisation ID missing");
      return;
    }
    setIsFieldsSaving(true);
    try {
      const res = await updateLeatFieldMappings.mutateAsync({
        organisation_id: userProfile.organisation_id,
        field_mappings: fieldMappings,
      });
      if (res?.data?.status === "success") {
        const saved = normalizeLeatFieldMappings(res.data.field_mappings);
        setFieldMappings(saved);
        setOriginalFieldMappings(saved);
        queryClient.invalidateQueries(["leat-mappings", userProfile.organisation_id]);
        queryClient.invalidateQueries(["leat-contact-attributes", userProfile.organisation_id]);
        setToastMessage("Data field mappings saved successfully");
      } else {
        setToastMessage(res?.data?.message || "Failed to save data field mappings");
      }
    } catch (err) {
      setToastMessage(err?.message || "Failed to save data field mappings");
    } finally {
      setIsFieldsSaving(false);
    }
  };

  useEffect(() => {
    if (leatShopsData) {
      setLeatShops((leatShopsData.data.shops || []).map((l) => ({ id: String(l.uuid ?? l.id ?? ""), name: l.name })));
    }
  }, [leatShopsData]);

  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrand = (brandsData || []).find((b) => b.is_default);
      const nonDefault = (brandsData || []).filter((b) => !b.is_default);
      setBconnectBrands(nonDefault || []);
      const defaultLocations = (locationsData || []).filter((loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null);
      setIndependentLocations(defaultLocations || []);
    } else {
      setBconnectBrands([]);
      setIndependentLocations([]);
    }
  }, [brandsData, locationsData]);

  // Build a map of brand -> its locations (prefer embedded brandsData[].locations)
  const brandLocationsMap = useMemo(() => {
    const map = {};
    if (!bconnectBrands) return map;

    bconnectBrands.forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter((loc) => String(loc.brand_id) === String(b.id));
      }
    });

    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  // Also provide a flat list of all brand-owned locations
  const allBrandOwnedLocations = useMemo(() => Object.values(brandLocationsMap).flat(), [brandLocationsMap]);

  // Validation / dirty detection (brand-owned)
  const hasBrandSectionChanges = useMemo(() => {
    const brandLocations = allBrandOwnedLocations || [];

    // must have at least one populated row that changed
    const hasAtLeastOne = brandLocations.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { leat_shop_uuid: "" };
      const currList = String(curr.leat_shop_uuid || "");
      const origList = String(orig.leat_shop_uuid || "");
      if (!currList) return false;
      return currList !== origList;
    });

    return Boolean(hasAtLeastOne);
  }, [allBrandOwnedLocations, locationMappings, originalLocationMappings]);

  // independent locations dirty detection
  const hasIndependentLocationChanges = useMemo(() => {
    const indep = independentLocations || [];
    const hasAtLeastOne = indep.some((loc) => {
      const locId = loc.id ?? loc.value ?? loc.location_id;
      const key = String(locId);
      const curr = locationMappings[key] || {};
      const orig = originalLocationMappings[key] || { leat_shop_uuid: "" };
      const currList = String(curr.leat_shop_uuid || "");
      const origList = String(orig.leat_shop_uuid || "");
      if (!currList) return false;
      return currList !== origList;
    });
    return Boolean(hasAtLeastOne);
  }, [independentLocations, locationMappings, originalLocationMappings]);

  // API token dirty detection
  const hasApiTokenChanges = useMemo(() => {
    return apiKey !== originalApiToken;
  }, [apiKey, originalApiToken]);

  const hasFieldMappingChanges = useMemo(() => {
    return JSON.stringify(fieldMappings) !== JSON.stringify(originalFieldMappings);
  }, [fieldMappings, originalFieldMappings]);

  // Handlers for mapping changes
  const handleLocationLeatListChange = (bconnectLocationId, leatListId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { leat_shop_uuid: "", isMapped: false };
      next[key] = { ...existing, leat_shop_uuid: leatListId, isMapped: Boolean(leatListId) };
      return next;
    });
  };

  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandListSelections((prev) => {
      const next = { ...(prev || {}) };
      next[String(brandId)] = listId;
      return next;
    });
  };

  const handleApplyBrandList = (brandId) => {
    const selectedList = brandListSelections[String(brandId)];
    if (!selectedList) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      (brandLocs || []).forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { leat_shop_uuid: "", isMapped: false };
        next[key] = { ...existing, leat_shop_uuid: selectedList, isMapped: Boolean(selectedList) };
      });
      return next;
    });
  };

  // Permission dialog
  const handleDisconnectClick = () => {
    if (isOwner) {
      setShowConfirmDialog(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  if (!initialItems) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>No locations available.</Text>
      </Flex>
    );
  }

  if (isBrandsLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ height: "200px" }}>
        <Text>Loading Leat integration data...</Text>
      </Flex>
    );
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Leat"
        subtitle="Map locations to Leat shops"
        image_url="leat.jpeg"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        {/* API token section */}
        <ActiveIntegrateCard.Section
          name="API token"
          subtitle="Update the Leat API token used for this integration"
        >
          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            {/* Row 1: Input + buttons */}
            <Flex direction="row" align="center" gap="2" style={{ width: "100%" }}>
              <input
                value={apiKey}
                onChange={handleApiTokenChange}
                type={showApiToken ? "text" : "password"}
                placeholder={hasStoredApiToken ? "Stored API token (hidden)" : "Enter Leat API token"}
                aria-label="Leat API token"
                disabled={!isOwner}
                style={{
                  flex: 1,
                  height: 36,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: `1px solid ${isApiTokenValid ? "var(--gray-a7)" : "var(--red-8)"}`,
                  boxSizing: "border-box",
                  background: isOwner ? "var(--white)" : "var(--gray-3)",
                }}
              />

              <Button size="1" variant="soft" onClick={() => setShowApiToken((s) => !s)}>
                {showApiToken ? "Hide" : "Show"}
              </Button>

              <Button
                size="1"
                variant="solid"
                color="blue"
                onClick={handleSaveApiToken}
                loading={isApiSaving}
                disabled={!isOwner || isApiSaving || !hasApiTokenChanges}
              >
                Save changes
              </Button>
            </Flex>

            {/* Row 2: Inline validation */}
            {!isApiTokenValid && (
              <Text size="1" weight="regular" color="red">
                {apiValidationMessage || "Please enter a valid API token."}
              </Text>
            )}
            {/* Row 2b: Success message */}
            {apiSaveSuccess && (
              <Text size="1" weight="medium" color="green">
                API token validated and saved successfully.
              </Text>
            )}

            {/* Row 3: Stored key hint */}
            {hasStoredApiToken && (
              <Text size="2" style={{ color: "var(--gray-a9)" }}>
                An API token is stored (hidden). Enter a new key to update it, or leave as-is.
              </Text>
            )}

            {/* Row 4: Subtle helper text */}
            <Text size="2" style={{ color: "var(--gray-a9)" }}>
              Changing the API token will validate it immediately. Existing mapping activation state will be preserved.
            </Text>
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Visit sync"
          subtitle="Optionally sync guest visits to Leat (Visits API)"
        >
          <Flex direction="row" align="center" justify="between" style={{ width: "100%" }}>
            <Text size="2" color="gray">
              Enable visit sync for hospitality connections
            </Text>
            <Switch
              size="2"
              checked={visitsEnabled}
              disabled={!isOwner || isVisitsSaving}
              onCheckedChange={handleVisitsToggle}
            />
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section
          name="Data fields"
          subtitle="Map captive portal fields to Leat contact columns. Fields a guest did not enter are skipped. Missing columns are created when you save."
        >
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <LeatDataFieldsSection
              fieldMappings={fieldMappings}
              attributes={leatAttributesData?.data?.attributes || []}
              onChange={handleFieldMappingChange}
              disabled={!isOwner}
            />
            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={handleSaveFieldMappings}
                loading={isFieldsSaving}
                disabled={!isOwner || isFieldsSaving || !hasFieldMappingChanges}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        {/* BRANDS section: grouped bconnect brands with their locations */}
        <ActiveIntegrateCard.Section name="Brands" subtitle="Group: b connect brands with each of their locations">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "48%" }}>b connect location</Text>
              <Text size="2" weight="medium" style={{ width: "48%" }}>Leat shop</Text>
            </Flex>

            {(bconnectBrands || []).length > 0 ? (
              (bconnectBrands || []).map((b) => {
                const brandLocations = brandLocationsMap[b.id] || [];
                return (
                  <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                    {/* Brand header */}
                    <Flex direction="row" style={{ width: "100%", alignItems: "center", gap: 8 }}>
                      <Text size="2" weight="medium" style={{ flex: "1 1 0%", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        [{b.name}]
                      </Text>

                      {/* Show brand-level apply only when brand has locations */}
                      {(brandLocations || []).length > 0 && (
                        <Flex style={{ marginLeft: "8px", gap: 8, alignItems: "center", flexShrink: 0 }}>
                          <Select.Root
                            value={brandListSelections[String(b.id)] || ""}
                            onValueChange={(v) => handleBrandListSelectionChange(b.id, v)}
                            disabled={!isOwner}
                            style={{ width: 220, minWidth: 120, flexShrink: 0 }}
                          >
                            <Select.Trigger placeholder="Apply shop to brand..." style={{ width: "220px", boxSizing: "border-box" }}>
                              <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }}
                                title={brandListSelections[String(b.id)] ? (leatShops.find((bl) => String(bl.id) === String(brandListSelections[String(b.id)]))?.name || "") : "Apply shop to brand..."}
                              >
                                {brandListSelections[String(b.id)] ? (leatShops.find((bl) => String(bl.id) === String(brandListSelections[String(b.id)]))?.name || "") : "Apply shop to brand..."}
                              </div>
                            </Select.Trigger>
                            <Select.Content side="bottom" align="start" sideOffset={4}>
                              {leatShops.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                            </Select.Content>
                          </Select.Root>

                          <Button size="1" variant="soft" onClick={() => handleApplyBrandList(b.id)} disabled={!isOwner || !brandListSelections[String(b.id)]} style={{ flexShrink: 0 }}>
                            Apply
                          </Button>
                        </Flex>
                      )}
                    </Flex>

                    {/* Locations under the brand */}
                    {(brandLocations || []).length > 0 ? (
                      brandLocations.map((loc) => {
                        const locId = loc.id ?? loc.value ?? loc.location_id;
                        const key = String(locId);
                        const mapping = locationMappings[key] || {};
                        return (
                          <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                            <Flex style={{ width: "48%", minWidth: 0 }}>
                              <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                                <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                                  {loc.name ?? loc.label ?? ""}
                                </Text>
                              </Flex>
                            </Flex>

                            <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                              <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                                <ArrowRightIcon width="16" height="16" />
                              </IconButton>
                            </Flex>

                            <Flex style={{ width: "48%", minWidth: 0 }}>
                              <Select.Root value={mapping.leat_shop_uuid || ""} onValueChange={(v) => handleLocationLeatListChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                                <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                                  <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.leat_shop_uuid ? (leatShops.find((bl) => String(bl.id) === String(mapping.leat_shop_uuid))?.name || "") : "Select a Leat shop"}>
                                    {mapping.leat_shop_uuid ? (leatShops.find((bl) => String(bl.id) === String(mapping.leat_shop_uuid))?.name || "") : "Select a Leat shop"}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {leatShops.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                                </Select.Content>
                              </Select.Root>
                            </Flex>
                          </Flex>
                        );
                      })
                    ) : (
                      <Flex justify="center" style={{ padding: "8px 0 16px 0" }}>
                        <Text size="2" style={{ color: "var(--gray-a9)" }}>No locations for this brand</Text>
                      </Flex>
                    )}
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No brands found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("brands")}
                loading={isBrandsSaving}
                disabled={!isOwner || isBrandsSaving || !hasBrandSectionChanges}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>

        {/* LOCATIONS section (independent / default-brand locations) */}
        <ActiveIntegrateCard.Section name="Locations" subtitle="Map independent b connect locations to a Leat shop">
          <Flex direction="column" gap="4" style={{ width: "100%" }}>
            <Flex direction="row" justify="between" style={{ width: "100%" }}>
              <Text size="2" weight="medium" style={{ width: "48%" }}>b connect location</Text>
              <Text size="2" weight="medium" style={{ width: "48%" }}>Leat shop</Text>
            </Flex>

            {(independentLocations || []).length > 0 ? (
              (independentLocations || []).map((loc) => {
                const locId = loc.id ?? loc.value ?? loc.location_id;
                const key = String(locId);
                const mapping = locationMappings[key] || {};

                return (
                  <Flex key={key} direction="row" justify="between" style={{ width: "100%" }}>
                    <Flex style={{ width: "48%", minWidth: 0 }}>
                      <Flex style={{ width: "100%", backgroundColor: "var(--gray-3)", border: "1px solid var(--gray-a7)", borderRadius: "var(--radius-2)", padding: "0 var(--space-2)", height: "34px", alignItems: "center", overflow: "hidden" }}>
                        <Text size="2" title={loc.name ?? loc.label ?? ""} style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%", color: "var(--gray-a11)" }}>
                          {loc.name ?? loc.label ?? ""}
                        </Text>
                      </Flex>
                    </Flex>

                    <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                      <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                        <ArrowRightIcon width="16" height="16" />
                      </IconButton>
                    </Flex>

                    <Flex style={{ width: "48%", minWidth: 0 }}>
                      <Select.Root value={mapping.leat_shop_uuid || ""} onValueChange={(v) => handleLocationLeatListChange(key, v)} disabled={!isOwner} style={{ width: "100%" }}>
                        <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
                          <div style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", width: "100%" }} title={mapping.leat_shop_uuid ? (leatShops.find((bl) => String(bl.id) === String(mapping.leat_shop_uuid))?.name || "") : "Select a Leat shop"}>
                            {mapping.leat_shop_uuid ? (leatShops.find((bl) => String(bl.id) === String(mapping.leat_shop_uuid))?.name || "") : "Select a Leat shop"}
                          </div>
                        </Select.Trigger>
                        <Select.Content side="bottom" align="start" sideOffset={4}>
                          {leatShops.map((bl) => <Select.Item key={bl.id} value={bl.id} title={bl.name}>{bl.name}</Select.Item>)}
                        </Select.Content>
                      </Select.Root>
                    </Flex>
                  </Flex>
                );
              })
            ) : (
              <Flex justify="center" style={{ padding: "20px 0" }}>
                <Text>No locations found.</Text>
              </Flex>
            )}

            <Flex justify="end">
              <Button
                size="2"
                variant="solid"
                color="blue"
                onClick={() => handleSaveLocationMappings("independent")}
                loading={isIndependentSaving}
                disabled={!isOwner || isIndependentSaving || !hasIndependentLocationChanges}
              >
                Save changes
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      {/* Confirm dialog */}
      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Leat</Dialog.Title>
          <Dialog.Description size="2" mb="4">Customer data will stop syncing with Leat.</Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" color="red" onClick={handleDeactivate} loading={isDeactivating} disabled={isDeactivating}>Disconnect</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Permission Alert Dialog */}
      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
